import { createAction, handleActions } from "redux-actions";

import {
  api,
  checkEmptyObject
} from "../../services";

import {
  phonesInterface,
  rootReducerInterface,
} from "../../typings/interfaces";

import { PhoneInterface } from "../../models";

// CONSTANTS
const PHONE_LIST_GET = "PHONE_LIST_GET";
const PHONE_LIST_CLEAR = "PHONE_LIST_CLEAR";
const PHONE_GET = "PHONE_GET";
const PHONE_CREATE = "PHONE_CREATE";
const PHONE_UPDATE = "PHONE_UPDATE";
const PHONE_SET_PRIMARY = "PHONE_SET_PRIMARY";
const PHONE_DELETE = "PHONE_DELETE";
const PHONE_SEND_PHONE_VERIFICATION_CODE = "PHONE_SEND_PHONE_VERIFICATION_CODE";
const PHONE_SEND_ADMIN_PHONE_VERIFICATION_EMAIL = "PHONE_SEND_ADMIN_PHONE_VERIFICATION_EMAIL"
const PHONE_VERIFY_PHONE_VERIFICATION_CODE = "PHONE_VERIFY_PHONE_VERIFICATION_CODE";
const PHONE_GET_SMS_MESSAGE_STATUS = "PHONE_GET_SMS_MESSAGE_STATUS";


// ACTIONS

// Get phones list by user id
export const getPhones = createAction(
  PHONE_LIST_GET,
  async (
    userId: string,
    callback?: (phones: PhoneInterface[] | Boolean) => void
  ) => {
    try {
      const phones = await api.Phone.list(
        { userId }
      );

      if (phones.length == 0) {
        callback && callback(false);
        return [];
      }

      callback && callback(phones);
      return phones;
    } catch (error) {
      callback && callback(false);
      return [];
    }
  }
);

// clear phones list
export const clearPhones = createAction(
  PHONE_LIST_CLEAR,
  (callback?: () => void) => {
    callback && callback();
    return [];
  }
);

// Get phone by id
export const getPhone = createAction(
  PHONE_GET,
  async (
    id: string,
    callback?: (phone: PhoneInterface | Boolean) => void
  ) => {
    try {
      const phone = await api.Phone.single(id);

      if (!checkEmptyObject(phone)) {
        callback && callback(null);
        return null;
      }

      callback && callback(phone);
      return phone;
    } catch (error) {
      callback && callback(null);
      return null;
    }
  }
);

// Create phone
export const createPhone = createAction(
  PHONE_CREATE,
  async (
    data: PhoneInterface,
    callback?: (response: any) => void
  ) => {
    try {
      const phone = await api.Phone.create(data);

      if (!checkEmptyObject(phone)) {
        callback && callback(null);
        return null;
      }

      callback && callback({ phone });
      return phone;
    } catch (error) {
      callback && callback({ error });

      return null;
    }
  }
);

// Update phone
export const updatePhone = createAction(
  PHONE_UPDATE,
  async (
    id: string,
    data: PhoneInterface,
    callback?: (phone: PhoneInterface | Boolean) => void
  ) => {
    try {
      const phone = await api.Phone.update(id, data);

      if (!checkEmptyObject(phone)) {
        callback && callback(null);
        return null;
      }

      callback && callback(phone);
      return phone;
    } catch (error) {
      callback && callback(null);
      return null;
    }
  }
);

// Delete phone
export const deletePhone = createAction(
  PHONE_DELETE,
  async (
    id: string,
    callback?: (success: Boolean | string) => void
  ) => {
    try {
      const success = await api.Phone.delete(id);
      callback && callback(id);
      return id;
    } catch (error) {
      callback && callback(false);
      return false;
    }
  }
);

// set primary phone
export const setPrimaryPhone = createAction(
  PHONE_SET_PRIMARY,
  async (
    id: string,
    callback?: (success: Boolean | string) => void
  ) => {
    try {
      const success = await api.Phone.setPrimary(id);
      callback && callback(id);
      return id;
    } catch (error) {
      callback && callback(false);
      return false;
    }
  }
);

// Send phone verification code
export const sendPhoneVerificationCode = createAction(
  PHONE_SEND_PHONE_VERIFICATION_CODE,
  async (
    id: string,
    callback?: (success: Boolean) => void
  ) => {
    try {
      const success = await api.Phone.sendPhoneVerificationCode(id); // Get profile after respomse to get cooldown time flag
      callback && callback(true);
      return true;
    } catch (error) {
      callback && callback(false);
      return false;
    }
  }
);

export const sendAdminPhoneVerificationEmail = createAction(
  PHONE_SEND_ADMIN_PHONE_VERIFICATION_EMAIL,
  async (
    id: string,
    callback?: (success: Boolean) => void
  ) => {
    try {
      const success = await api.Phone.sendAdminPhoneVerificationEmail(id);
      callback && callback(true);
      return true;
    } catch (error) {
      callback && callback(false);
      return false;
    }
  }
);

// Verify phone verification code
export const verifyPhoneVerificationCode = createAction(
  PHONE_VERIFY_PHONE_VERIFICATION_CODE,
  async (
    id: string,
    code: string,
    callback?: (success: Boolean | string) => void
  ) => {
    try {
      const success = await api.Phone.verifyPhoneVerificationCode(id, code);
      callback && callback(id);
      return id;
    } catch (error) {
      callback && callback(false);
      return false;
    }
  }
);

// Get SMS message status
export const getSMSMessageStatus = createAction(
  PHONE_GET_SMS_MESSAGE_STATUS,
  async (
    id: string,
    callback?: (success: Boolean | string) => void
  ) => {
    try {
      const status = await api.Phone.getSMSMessageStatus(id);

      if (!status) {
        callback && callback(null);
        return null;
      }

      callback && callback(status);
      return status;
    } catch (error) {
      callback && callback(false);
      return false;
    }
  }
);

// SELECTORS
export const selectPhones = (state: rootReducerInterface) =>
  state.phone.phones;

export const selectPhone = (state: rootReducerInterface) =>
  state.phone.phone;

export const selectSMSMessageStatus = (state: rootReducerInterface) =>
  state.phone.status;

// INITIAL STATE
const initialState: phonesInterface = {
  phones: [],
  phone: null,
  status: null,
};

// REDUCERS
const phoneReducers = {
  setPhones: (state: phonesInterface, { payload }) => ({
    ...state,
    phones: !payload ? [] : [...payload]
  }),
  clearPhones: (state: phonesInterface) => ({
    ...state,
    phones: [],
  }),
  setPhone: (state: phonesInterface, { payload }) => ({
    ...state,
    phone: !payload ? null : { ...payload }
  }),
  createPhone: (state: phonesInterface, { payload }) => ({
    ...state,
    phones: payload ? [...state.phones, { ...payload }] : state.phones,
    phone: !payload ? null : { ...payload }
  }),
  updatePhone: (state: phonesInterface, { payload }) => ({
    ...state,
    phones: payload ? state.phones.map(phone => phone.id === payload.id ? { ...payload } : phone) : state.phones,
    phone: !payload ? null : { ...payload }
  }),
  setPrimaryPhone: (state: phonesInterface, { payload }) => ({
    ...state,
    phones: state.phones.map(phone => phone.id === payload ? { ...phone, isPrimary: true } : phone),
    phone: payload ? { ...state.phone, isPrimary: true } : state.phone
  }),
  deletePhone: (state: phonesInterface, { payload }) => ({
    ...state,
    phones: state.phones.filter(phone => phone.id !== payload),
    phone: null
  }),
  sendPhoneVerificationCode: (state: phonesInterface, { payload }) => ({
    ...state,
  }),
  sendAdminPhoneVerificationEmail: (state: phonesInterface, { payload }) => ({
    ...state,
  }),
  verifyPhoneVerificationCode: (state: phonesInterface, { payload }) => ({
    ...state,
    phones: state.phones.map(phone => phone.id === payload ? { ...phone, isVerified: true } : phone),
    phone: payload ? { ...state.phone, isVerified: true } : state.phone
  }),
  getSMSMessageStatus: (state: phonesInterface, { payload }) => ({
    ...state,
    status: payload
  }),
};

export default handleActions(
  {
    [PHONE_LIST_GET]: phoneReducers.setPhones,
    [PHONE_LIST_CLEAR]: phoneReducers.clearPhones,
    [PHONE_GET]: phoneReducers.setPhone,
    [PHONE_CREATE]: phoneReducers.createPhone,
    [PHONE_UPDATE]: phoneReducers.updatePhone,
    [PHONE_SET_PRIMARY]: phoneReducers.setPrimaryPhone,
    [PHONE_DELETE]: phoneReducers.deletePhone,
    [PHONE_SEND_PHONE_VERIFICATION_CODE]: phoneReducers.sendPhoneVerificationCode,
    [PHONE_SEND_ADMIN_PHONE_VERIFICATION_EMAIL]: phoneReducers.sendAdminPhoneVerificationEmail,
    [PHONE_VERIFY_PHONE_VERIFICATION_CODE]: phoneReducers.verifyPhoneVerificationCode,
    [PHONE_GET_SMS_MESSAGE_STATUS]: phoneReducers.getSMSMessageStatus,
  },
  initialState
);



// WEBPACK FOOTER //
// ./src/routes/Phone/phone.ducks.ts